'use client';

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'advika_cookie_consent_v1';

type ConsentChoice = { analytics: boolean; advertising: boolean; savedAt: string; version: 1 };
type GtagWindow = Window & { gtag?: (...args: unknown[]) => void };

function applyConsent(choice: ConsentChoice) {
  (window as GtagWindow).gtag?.('consent', 'update', {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: choice.advertising ? 'granted' : 'denied',
    ad_user_data: choice.advertising ? 'granted' : 'denied',
    ad_personalization: choice.advertising ? 'granted' : 'denied',
  });
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as ConsentChoice | null;
      if (saved?.version === 1) {
        setAnalytics(saved.analytics === true);
        setAdvertising(saved.advertising === true);
        setHasChoice(true);
        applyConsent(saved);
        return;
      }
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }
    setVisible(true);
  }, []);

  const save = (nextAnalytics: boolean, nextAdvertising: boolean) => {
    const choice: ConsentChoice = {
      analytics: nextAnalytics,
      advertising: nextAdvertising,
      savedAt: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    applyConsent(choice);
    setAnalytics(nextAnalytics);
    setAdvertising(nextAdvertising);
    setHasChoice(true);
    setVisible(false);
    setCustomizing(false);
  };

  if (!visible) {
    return hasChoice ? (
      <button className="advika-cookie-settings" onClick={() => setVisible(true)} type="button">
        Cookie settings
      </button>
    ) : null;
  }

  return (
    <div className="advika-consent-backdrop" role="presentation">
      <section className="advika-consent" role="dialog" aria-modal="true" aria-labelledby="advika-consent-title">
        <div className="advika-consent-copy">
          <span className="advika-consent-kicker">YOUR PRIVACY</span>
          <h2 id="advika-consent-title">Choose how Advika uses cookies</h2>
          <p>
            Necessary storage keeps the site secure and usable. With your permission, analytics helps us improve Advika;
            advertising storage supports measurement and personalization. Read our <a href="/cookies">Cookie Policy</a>.
          </p>
        </div>

        {customizing ? (
          <div className="advika-consent-options">
            <label><span><strong>Necessary</strong><small>Security, navigation and saved privacy choices.</small></span><input type="checkbox" checked disabled /></label>
            <label><span><strong>Analytics</strong><small>Helps us understand usage and improve the product.</small></span><input type="checkbox" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} /></label>
            <label><span><strong>Advertising</strong><small>Allows advertising measurement and personalization.</small></span><input type="checkbox" checked={advertising} onChange={(event) => setAdvertising(event.target.checked)} /></label>
          </div>
        ) : null}

        <div className="advika-consent-actions">
          {customizing ? <button className="secondary" onClick={() => save(analytics, advertising)} type="button">Save choices</button> : <button className="secondary" onClick={() => setCustomizing(true)} type="button">Manage choices</button>}
          <button className="secondary" onClick={() => save(false, false)} type="button">Reject non-essential</button>
          <button className="primary" onClick={() => save(true, true)} type="button">Accept all</button>
        </div>
      </section>
      <style jsx global>{`
        .advika-consent-backdrop{position:fixed;inset:0;background:rgba(9,12,28,.4);z-index:100000;display:flex;align-items:flex-end;justify-content:center;padding:24px}
        .advika-consent{width:min(1120px,100%);background:#fff;border:1px solid #dfe5f1;border-radius:24px;box-shadow:0 24px 70px rgba(10,17,45,.2);padding:28px;display:grid;gap:22px;color:#11182b}
        .advika-consent-copy h2{font-size:24px;line-height:1.2;margin:6px 0 9px}.advika-consent-copy p{margin:0;color:#5f6f89;line-height:1.6;max-width:900px}.advika-consent-copy a{color:#4f46e5;text-decoration:underline}.advika-consent-kicker{font-size:12px;letter-spacing:.16em;font-weight:800;color:#6547ef}
        .advika-consent-options{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.advika-consent-options label{border:1px solid #dfe5f1;border-radius:15px;padding:15px;display:flex;justify-content:space-between;gap:16px;align-items:center}.advika-consent-options span{display:grid;gap:4px}.advika-consent-options small{color:#6b7890;line-height:1.35}.advika-consent-options input{width:20px;height:20px;accent-color:#5b46eb}
        .advika-consent-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}.advika-consent-actions button,.advika-cookie-settings{font:inherit;font-weight:750;border-radius:12px;padding:12px 17px;cursor:pointer}.advika-consent-actions .primary{border:1px solid #5542e8;background:#5542e8;color:#fff}.advika-consent-actions .secondary{border:1px solid #d7deeb;background:#fff;color:#1d2940}.advika-cookie-settings{position:fixed;left:16px;bottom:16px;z-index:99990;padding:8px 12px;border:1px solid #d7deeb;background:#fff;color:#34405a;box-shadow:0 6px 22px rgba(10,17,45,.12);font-size:12px}
        @media(max-width:720px){.advika-consent-backdrop{padding:12px}.advika-consent{padding:20px;border-radius:18px;max-height:calc(100vh - 24px);overflow:auto}.advika-consent-options{grid-template-columns:1fr}.advika-consent-actions{display:grid}.advika-consent-actions button{width:100%}}
      `}</style>
    </div>
  );
}
